import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  CheckCircle2,
  Calendar,
  ClipboardCheck,
  ShieldCheck,
  MessageSquare,
  Sparkles,
} from "lucide-react";
import useSEO from "@/lib/useSEO";
import useHomepageImages from "@/lib/useHomepageImages";
import CmsImage from "@/components/CmsImage";
import RelatedServices from "@/components/RelatedServices";
import api from "@/lib/api";
import {
  WHO_WE_SERVE,
  COMMERCIAL_INCLUDES,
  COMMERCIAL_FREQ,
  COMMERCIAL_DISCLAIMER,
  IMAGES,
} from "@/data/content";
import { useBusiness } from "@/contexts/SettingsContext";

const reasons = [
  { icon: Calendar, label: "Reliable scheduling", desc: "Recurring plans that fit your business hours." },
  { icon: MessageSquare, label: "Clear communication", desc: "Quick replies, simple coordination, no surprises." },
  { icon: ShieldCheck, label: "Professional presentation", desc: "A clean, organized appearance for your team and guests." },
  { icon: ClipboardCheck, label: "Flexible cleaning plans", desc: "Daily, weekly, biweekly, monthly, or custom." },
  { icon: Sparkles, label: "Local Sacramento-area service", desc: "Family business based right in your community." },
  { icon: CheckCircle2, label: "Custom quotes", desc: "Tailored to your space, scope, and frequency." },
];

export default function Commercial() {
  const BUSINESS = useBusiness();
  const { pick: pickImage } = useHomepageImages();
  const heroImg = pickImage("commercial_2", IMAGES.heroCommercial, "Bright, modern commercial office space");
  const [related, setRelated] = useState([]);
  useEffect(() => {
    let off = false;
    api.get("/public/page-links/commercial-cleaning")
      .then((r) => !off && setRelated(r.data?.related_links || []))
      .catch(() => {});
    return () => { off = true; };
  }, []);
  useSEO({
    title: "Commercial & Office Cleaning in Sacramento | P & A Cleaning",
    description:
      "Reliable office cleaning, business cleaning, restrooms, breakrooms, and common areas in Sacramento. Flexible recurring plans and custom quotes.",
  });

  return (
    <div data-testid="page-commercial">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#F8FAFC] to-white">
        <div className="absolute inset-0 bg-dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-[#7C3AED] shadow-soft">
                For Business
              </span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-[#0F172A] tracking-tight leading-[1.05]">
                Commercial & Office Cleaning{" "}
                <span className="gradient-text">in Sacramento</span>
              </h1>
              <p className="mt-6 text-lg text-[#475569] max-w-xl">
                Reliable cleaning for offices, small businesses, property
                managers, retail spaces, restrooms, breakrooms, and common areas.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/request-estimate"
                  data-testid="commercial-hero-cta-quote"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors shadow-soft"
                >
                  Request Commercial Quote <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="commercial-hero-cta-walkthrough"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#1E3A8A] font-semibold border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  Schedule a Walkthrough
                </Link>
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="commercial-hero-cta-call"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#34D399] text-[#0F172A] font-semibold hover:bg-[#10B981] transition-colors"
                >
                  <Phone className="w-4 h-4" /> Call / Text Now
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-card border border-white">
                <CmsImage
                  src={heroImg.src}
                  alt={heroImg.alt}
                  loading={heroImg.loading}
                  testId="commercial-hero-image"
                  className="w-full h-[420px] lg:h-[520px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#7C3AED]">
              Who we serve
            </p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0F172A]">
              Built for working businesses
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {WHO_WE_SERVE.map(({ label, icon: Icon }) => (
              <div
                key={label}
                data-testid={`commercial-who-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bg-[#F8FAFC] border border-slate-100 rounded-2xl p-5 flex flex-col items-start gap-3 hover:shadow-soft transition-shadow"
              >
                <span className="w-10 h-10 rounded-xl bg-white border border-slate-100 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#1E3A8A]" />
                </span>
                <p className="text-sm font-semibold text-[#0F172A] leading-snug">
                  {label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INCLUDES + FREQ */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-8 lg:p-10 border border-slate-100 shadow-soft">
              <p className="text-sm font-semibold uppercase tracking-wider text-[#1E3A8A]">
                Services may include
              </p>
              <h3 className="mt-2 text-2xl sm:text-3xl font-display font-semibold text-[#0F172A]">
                Commercial cleaning checklist
              </h3>
              <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {COMMERCIAL_INCLUDES.map((line) => (
                  <li
                    key={line}
                    className="flex items-start gap-2 text-sm text-[#0F172A]"
                  >
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#34D399] flex-shrink-0" />
                    {line}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-3xl p-8 lg:p-10 border border-slate-100 shadow-soft">
              <p className="text-sm font-semibold uppercase tracking-wider text-[#047857]">
                Recurring options
              </p>
              <h3 className="mt-2 text-2xl sm:text-3xl font-display font-semibold text-[#0F172A]">
                Pick a schedule that fits
              </h3>
              <ul className="mt-6 space-y-3">
                {COMMERCIAL_FREQ.map((line) => (
                  <li
                    key={line}
                    data-testid={`commercial-freq-${line.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="flex items-center justify-between gap-2 bg-[#F8FAFC] border border-slate-100 rounded-2xl px-5 py-4"
                  >
                    <span className="font-medium text-[#0F172A]">{line}</span>
                    <ArrowRight className="w-4 h-4 text-[#A78BFA]" />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY BUSINESSES */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#1E3A8A]">
              Why businesses choose us
            </p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0F172A]">
              Cleaning your team can count on
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {reasons.map(({ icon: Icon, label, desc }) => (
              <div
                key={label}
                className="bg-[#F8FAFC] border border-slate-100 rounded-2xl p-6"
              >
                <span className="w-11 h-11 rounded-xl bg-white border border-slate-100 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#1E3A8A]" />
                </span>
                <h3 className="mt-4 font-display font-semibold text-[#0F172A]">{label}</h3>
                <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED SERVICES (Internal Linking Manager, editable in Admin > Internal Links) */}
      <RelatedServices links={related} testId="commercial-related-services" />

      {/* COMMERCIAL CTA */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-[#0F172A] p-10 lg:p-16 text-white">
            <div className="relative max-w-3xl">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold leading-tight">
                Need reliable cleaning for your office or business?
              </h2>
              <p className="mt-4 text-white/80 text-lg">
                Tell us about your space and frequency — we'll respond with a
                custom quote and walkthrough options.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/request-estimate"
                  data-testid="commercial-final-cta-quote"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#1E3A8A] font-semibold hover:bg-slate-100 transition-colors"
                >
                  Request Commercial Quote <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="commercial-final-cta-walkthrough"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#34D399] text-[#0F172A] font-semibold hover:bg-[#10B981] transition-colors"
                >
                  Schedule a Walkthrough
                </Link>
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="commercial-final-cta-call"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors font-semibold"
                >
                  <Phone className="w-4 h-4" /> Call / Text Now
                </a>
              </div>
            </div>
          </div>

          <p
            data-testid="commercial-disclaimer"
            className="mt-8 text-xs text-[#475569] leading-relaxed bg-white border border-slate-100 rounded-2xl p-5"
          >
            <strong className="text-[#0F172A]">Pricing note:</strong>{" "}
            {COMMERCIAL_DISCLAIMER}
          </p>
        </div>
      </section>
    </div>
  );
}
